import { useCallback } from 'react';
import { useCreateFolder } from '@frameio/assets/operations';
import { useSetRenameItem } from '@frameio/project-tree/contexts';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { IconButton2, MenuButton, Tooltip } from '@frameio/vapor';
import {
  IconFolderClosed,
  IconFolderLocked,
  IconPlus,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ProjectNavigation_CreateFolderButton';

type Variant = 'icon-button' | 'menu-button';

type CreateFolderButtonProps = {
  parentId: string;
  projectId: string;
  restricted?: boolean;
  trackingSource?: string;
  variant?: Variant;
};

export function CreateFolderButton({
  parentId,
  projectId,
  restricted = false,
  trackingSource = 'ProjectNavProjectMenu',
  variant = 'icon-button',
}: CreateFolderButtonProps) {
  const setRenameItem = useSetRenameItem();
  const track = useTrackUnsafe();
  const createFolder = useCreateFolder({
    onSuccess({ id }) {
      setRenameItem({
        id,
        type: 'folder',
      });
    },
  });

  const handlePress = useCallback(() => {
    track('folder-created', {
      source: trackingSource,
      is_restricted: false,
    });
    createFolder({ projectId, parentId, restricted });
  }, [createFolder, parentId, projectId, restricted, track, trackingSource]);

  const label = restricted ? TEXT.createRestrictedFolder : TEXT.createFolder;

  return (
    <Tooltip placement="top" title={TEXT.createFolder}>
      {variant === 'menu-button' ? (
        <MenuButton
          iconBefore={restricted ? <IconFolderLocked /> : <IconFolderClosed />}
          label={label}
          onPress={handlePress}
        >
          {label}
        </MenuButton>
      ) : (
        <IconButton2
          icon={<IconPlus />}
          label={label}
          onPress={handlePress}
          size="small"
          variant="tertiary"
        />
      )}
    </Tooltip>
  );
}

TEXT.DEFINE({
  /** Tooltip and aria-label for creating a folder */
  createFolder: 'New Folder',
  /** Tooltip and aria-label for creating a restricted folder */
  createRestrictedFolder: 'New Restricted Folder',
});
